import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { get, has, find, mapValues, uniqBy, some, filter } from 'lodash';
import {
  Accordion,
  AccordionContent,
  AccordionGroup,
  Checkbox
} from 'core/controls';
import { Translate, withTranslate } from 'core/translate';
import { SvgIcon } from 'core/icons';
import ScrollTop from '../../common/ScrollTop';
import OptionsAccordionHeader from '../../common/options/OptionsAccordionHeader';
import DimensionsRoute from '../dimensions/DimensionsRoute';
import PriceWithDisclaimer from './PriceWithDisclaimer';
import BodyIcon from '../../common/icons/BodyIcon';
import { findClosestMsrp } from './findClosestMsrp';
import { withModelInfo } from '../../common/ModelInfoContext';

const getTruckDimension = values => {
  const ID = 351;
  if (has(values, ID)) {
    return {
      label: <Translate value="dimensions.bed.length" />,
      value: get(values, ID)
    };
  }
  return null;
};

const getVanDimension = (descriptions, values) => {
  const CARGO_ID = 371;
  const PASSENGER_ID = 372;
  if (has(values, PASSENGER_ID)) {
    return {
      label: get(descriptions, PASSENGER_ID),
      value: get(values, PASSENGER_ID)
    };
  }
  if (has(values, CARGO_ID)) {
    return {
      label: get(descriptions, CARGO_ID),
      value: get(values, CARGO_ID)
    };
  }
  return null;
};

const getDimension = (features, type, styleId) => {
  try {
    const values = get(features, ['styleIds', styleId, 'Dimensions']);
    const descriptions = mapValues(features.groups.Dimensions, 'description');

    switch (type) {
      case 'truck':
        return getTruckDimension(values);
      case 'van':
        return getVanDimension(descriptions, values);
      default:
        return null;
    }
  } catch (e) {
    return null;
  }
};

const DimensionsLink = ({ id, dimension, country }) => {
  if (!dimension) {
    return null;
  }
  const isCanada = country === 'CA';
  return (
    <Link
      to={`/config/dimensions/${id}`}
      className="config-option__featured-dimension config-option__featured-dimension--tf">
      <div className="config-option__featured-dimension-text">
        <span className="config-option__featured-dimension-value">
          {isCanada ? (
            <Fragment>{dimension.label} : </Fragment>
          ) : (
            <Fragment>{dimension.label}: </Fragment>
          )}
        </span>
        <span>{dimension.value}</span>
      </div>
    </Link>
  );
};

const DimensionsInfoIcon = ({ id, dimension }) => {
  if (!dimension) {
    return null;
  }
  return (
    <Link to={`/config/dimensions/${id}`}>
      <SvgIcon name="info" />
    </Link>
  );
};

class ConfigOption extends React.Component {
  onChange = () => {
    const { checked, option, onChange } = this.props;
    if (!checked) {
      onChange(option);
    }
  };

  render() {
    const {
      option,
      checked,
      disclaimer,
      dimension,
      analyticsKey,
      modelInfo
    } = this.props;
    const { bodyType, description, msrp, cabSize, message } = option;
    const { country } = modelInfo;

    return (
      <Checkbox
        value={bodyType}
        checked={checked}
        analyticsKey={analyticsKey}
        analyticsKey2={description}
        className="control--top option option--tf"
        onChange={this.onChange}>
        <BodyIcon cabSize={cabSize} className="option__icon--tf" />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 1 }}>
              <div className="option__title option__title--tf">
                {description}
              </div>
              <DimensionsLink
                id={bodyType}
                dimension={dimension}
                country={country}
              />
              <div className="option__disclaimer">
                <PriceWithDisclaimer
                  value={msrp}
                  message={message}
                  disclaimer={disclaimer}
                  analyticsKey={analyticsKey}
                  analyticsKey2={description}
                />
              </div>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <DimensionsInfoIcon id={bodyType} dimension={dimension} />
            </div>
          </div>
        </div>
      </Checkbox>
    );
  }
}

const NormalizeBodyTypeOptions = ({ matrix, children }) => {
  const { options, bodyTypes } = matrix;
  const mapped = options.map(option => {
    const { cabSize, description } = find(bodyTypes, { id: option.bodyType });
    return {
      ...option,
      description,
      cabSize
    };
  });
  return children(mapped);
};

class ConfigInner extends React.Component {
  onConfigChange = ({ bodyType, cabSize }) => {
    const { options, config, onChange } = this.props;
    const { driveType, trimName, engine, transmission } = config.selections;
    const msrp = config.msrp.totalMsrpWithOptions;

    const hasCabSizes = some(options, 'cabSize');
    const bodyFilter = hasCabSizes ? { cabSize } : { bodyType };
    const filteredOptions = filter(options, bodyFilter);

    const option =
      find(filteredOptions, { driveType, trimName, engine, transmission }) ||
      findClosestMsrp(filter(filteredOptions, { driveType, trimName }), msrp) ||
      findClosestMsrp(filter(filteredOptions, { driveType }), msrp) ||
      findClosestMsrp(filteredOptions, msrp);

    onChange(option);
  };

  render() {
    const {
      translate,
      options,
      config,
      features,
      type,
      disclaimer,
      modelInfo
    } = this.props;
    const { selections, bodyTypes } = config;

    const header = translate('component.common.select.config');
    const hasCabSizes = some(options, 'cabSize');
    const uniqueOptions = hasCabSizes
      ? uniqBy(options, 'cabSize')
      : uniqBy(options, 'description');

    return (
      <React.Fragment>
        <Accordion>
          <AccordionGroup>
            <OptionsAccordionHeader
              header={header}
              count={uniqueOptions.length}
              hideSingleCount={true}
            />
            <AccordionContent>
              {uniqueOptions.map(option => (
                <ConfigOption
                  key={option.bodyType}
                  option={option}
                  dimension={getDimension(features, type, option.styleId)}
                  disclaimer={disclaimer}
                  checked={
                    hasCabSizes
                      ? option.cabSize === selections.cabSize
                      : option.bodyType === selections.bodyType
                  }
                  onChange={this.onConfigChange}
                  modelInfo={modelInfo}
                />
              ))}
            </AccordionContent>
          </AccordionGroup>
        </Accordion>
        <DimensionsRoute features={features} bodyTypes={bodyTypes} />
      </React.Fragment>
    );
  }
}

const ConfigTrimFiltering = props => {
  const { matrix, onChange } = props;
  return (
    <ScrollTop>
      <NormalizeBodyTypeOptions matrix={matrix}>
        {options => (
          <ConfigInner {...props} options={options} onChange={onChange} />
        )}
      </NormalizeBodyTypeOptions>
    </ScrollTop>
  );
};

export default withModelInfo(withTranslate(ConfigTrimFiltering));
